import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { RiFileTextLine, RiAttachmentLine, RiDeleteBinLine, RiFileListLine, RiFileAddLine, RiInformationLine, RiLockLine } from 'react-icons/ri';
import contractService, { ContractStatus } from '../../services/contractService';
import companyService, { Company } from '../../services/companyService';
import authorizedSignerService, { AuthorizedSigner } from '../../services/authorizedSignerService';
import { companyContractWarningLines } from '../../utils/companyContractWarning';
import { showAlert, showConfirm } from '../../utils/alert';
import SearchableSelect from '../../components/ui/SearchableSelect';
import ValidatedInput from '../../components/ui/ValidatedInput';
import codeGeneratorService from '../../services/codeGeneratorService';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { getLocalDateString } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';

/**
 * FB-2026-0004 (amintra · บัญชี): หน้า /contracts/create สร้างได้ "สัญญาหลัก" เท่านั้น
 * ใส่แค่ "วันที่ทำสัญญา" — ไม่มีวันเริ่ม/วันสิ้นสุด (สัญญาแบบเปิด ตามแบบฟอร์ม 01 - สัญญาหลัก.docx)
 * รายการเช่าผูกกับสัญญาผ่าน "สัญญาแนบท้าย" (บันทึกส่ง-มอบ · 1 ฉบับ = 1 รายการเช่า) ที่เมนู ⋮
 * ในหน้าจัดการสัญญา (FB-2026-0006) — หน้านี้จึงไม่มีการเลือกรายการเช่าอีก
 */
const ContractCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate } = usePermission();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  // BUG-FE-DBLSUBMIT: disabled={loading} needs a re-render; a same-tick second
  // click still runs this handler. Guard synchronously.
  const submitGuard = useSubmitGuard();
  // G5 (MANUAL_AUDIT_SYSTEM_FINDINGS): เก็บ "เลขที่สัญญาที่ยังมีผล" ต่อบริษัท ใช้แค่แจ้งเตือน (ไม่บล็อก)
  // OBSERVATION 3: สรุปสัญญาของ "บริษัทที่เลือก" — นับจาก total ของ backend ไม่ใช่จาก items
  interface CompanyContractSummary {
    activeTotal: number;
    activeNumbers: string[];
    draftTotal: number;
    draftNumbers: string[];
    /** สัญญาหลัก (MAIN) ที่ยังไม่จบของบริษัทนี้ — ควรเพิ่มแนบท้ายที่ฉบับเดิมแทนการสร้างใหม่ */
    mainContracts: Array<{ id: number; number: string }>;
  }
  const [contractSummary, setContractSummary] = useState<CompanyContractSummary | null>(null);

  // ผู้มีอำนาจลงนาม
  const [availableSigners, setAvailableSigners] = useState<AuthorizedSigner[]>([]);
  const [selectedSignerIds, setSelectedSignerIds] = useState<number[]>([]);
  const [minRequiredSigners, setMinRequiredSigners] = useState(1);

  // ข้อมูลสัญญาหลัก
  const [contractNumber, setContractNumber] = useState('');
  const [contractDate, setContractDate] = useState(getLocalDateString()); // วันที่ทำสัญญา — default วันนี้
  const [termsConditions, setTermsConditions] = useState('');
  const [notes, setNotes] = useState('');

  // การแนบไฟล์
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [shouldShowSuccess, setShouldShowSuccess] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Unsaved changes warning
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isFormDirty);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const handleBack = () => {
    if (isFormDirty) {
      setShowBackConfirm(true);
    } else {
      navigate('/contracts');
    }
  };

  // ตรวจสอบ permission - ถ้าไม่มีสิทธิ์จะแสดง Access Denied
  const hasCreatePermission = canCreate('contracts');

  // โหลดข้อมูลบริษัททั้งหมด
  useEffect(() => {
    // BUG-S2074-002: ไม่มีสิทธิ์ = ไม่โหลดอะไร (ฟังก์ชันประกาศหลัง early-return → TDZ)
    if (!hasCreatePermission) return;
    loadCompanies();
    generateContractNumber();
  }, [hasCreatePermission]);

  // Track form dirty state
  // KD3 S2462: เทียบกับค่าตอนเปิดหน้า แทนการนับรอบ effect — dev StrictMode รัน effect ซ้ำ
  const initialFormRef = React.useRef({ selectedCompany, contractDate, termsConditions, notes, attachedFiles });
  useEffect(() => {
    const init = initialFormRef.current;
    if (
      selectedCompany !== init.selectedCompany ||
      contractDate !== init.contractDate ||
      termsConditions !== init.termsConditions || notes !== init.notes ||
      attachedFiles !== init.attachedFiles
    ) {
      setIsFormDirty(true);
    }
  }, [selectedCompany, contractDate, termsConditions, notes, attachedFiles]);

  // G-23: early-return ต้องอยู่หลัง hook ตัวสุดท้ายเสมอ (Rules of Hooks)
  if (!hasCreatePermission) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RiLockLine className="w-10 h-10 text-danger-500" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-2">ไม่มีสิทธิ์เข้าถึง</h1>
            <p className="text-secondary-600 mb-6">คุณไม่มีสิทธิ์ในการสร้างสัญญา กรุณาติดต่อผู้ดูแลระบบ</p>
            <button
              onClick={() => navigate('/contracts')}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              กลับไปหน้ารายการสัญญา
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // เลขที่สัญญาถูกใช้ไปแล้ว → ออกเลขใหม่ให้ (ช่องเลขที่เป็นอ่านอย่างเดียว ผู้ใช้เปลี่ยนเองไม่ได้)
  // ใช้ทั้งตอนตรวจซ้ำก่อนบันทึก และตอน backend ตอบ 400 เพราะอีกคนบันทึกเลขเดียวกันไปก่อน (BUG-S2079-002)
  const replaceTakenContractNumber = async (oldCode: string) => {
    try {
      const newCode = await codeGeneratorService.generateSingleCode('contract');
      setContractNumber(newCode);
      setCodeError(`เลขที่ ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว เลขที่ใหม่: ${newCode}`);
      setShouldShowSuccess(false);
    } catch (genError: any) {
      console.error('Error generating new contract number:', genError);
      setCodeError(`เลขที่ ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาลองใหม่`);
    }
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const generateContractNumber = async () => {
    try {
      const nextCodePreview = await codeGeneratorService.getNextCodePreview('contract', true);
      setContractNumber(nextCodePreview.next_code);
      setCodeError('');
    } catch (error) {
      console.error('Error getting contract number preview:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await companyService.getCompanies({ limit: 1000 });
      setCompanies(response.items);
    } catch (error) {
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถโหลดข้อมูลบริษัทได้', 'error');
    }
  };

  /**
   * สรุปสัญญาของบริษัทที่เลือก สำหรับคำเตือนก่อนสร้างสัญญาใหม่ (ไม่บล็อก)
   * OBSERVATION 3: ถามเฉพาะบริษัทที่เลือก แยกตามสถานะ และใช้ `total` จาก backend เป็นตัวนับ
   * FB-2026-0004: บอกสัญญาหลักที่มีอยู่แล้ว — ปกติบริษัทหนึ่งมีสัญญาหลักฉบับเดียว แล้วเพิ่มแนบท้ายต่อเครื่อง
   */
  const loadCompanyContractSummary = async (companyId: number) => {
    try {
      const [active, draft] = await Promise.all([
        // top_level_only: นับเฉพาะสัญญาระดับบนสุด (สัญญาหลัก/สัญญาแบบเดิม) — แนบท้ายไม่ใช่ "สัญญาหลักซ้ำ"
        // (เดิมแนบท้ายร่าง 2 ฉบับ = "มีร่างสัญญา 2 ฉบับ" ทั้งที่สัญญาหลักมีฉบับเดียวและอนุมัติแล้ว)
        contractService.getContracts({ company_id: companyId, status: ContractStatus.ACTIVE, limit: 50, top_level_only: true }),
        contractService.getContracts({ company_id: companyId, status: ContractStatus.DRAFT, limit: 50, top_level_only: true }),
      ]);
      const numbers = (res: { items: Array<{ id: number; contract_number?: string }> }) =>
        res.items.map((c) => c.contract_number || `#${c.id}`);
      setContractSummary({
        activeTotal: active.total,
        activeNumbers: numbers(active),
        draftTotal: draft.total,
        draftNumbers: numbers(draft),
        mainContracts: [...active.items, ...draft.items]
          .filter((c: any) => c.contract_type === 'MAIN')
          .map((c: any) => ({ id: c.id, number: c.contract_number || `#${c.id}` })),
      });
    } catch (error) {
      console.error('Error loading contract summary for company:', error);
      // เตือนไม่ได้ก็ไม่ควรขวางการสร้างสัญญา — ซ่อนกล่องเตือนไปเลยดีกว่าโชว์ตัวเลขที่อาจผิด
      setContractSummary(null);
    }
  };

  const handleCompanyChange = async (companyId: number) => {
    setSelectedCompany(companyId);
    setContractSummary(null);   // ล้างสรุปของบริษัทเดิม กันโชว์ตัวเลขข้ามบริษัท

    // Reset signer state เมื่อเปลี่ยนบริษัท
    setSelectedSignerIds([]);
    setAvailableSigners([]);
    setMinRequiredSigners(1);

    setCodeError('');
    setShouldShowSuccess(true);

    // G5: คำเตือนแบบไม่บล็อก — ค่าที่เลือกยังอยู่ ทำงานต่อได้ทันที
    if (companyId) {
      loadCompanyContractSummary(companyId);

      try {
        const signers = await authorizedSignerService.getActiveSignersByCompany(companyId);
        setAvailableSigners(signers);
        // Auto-select ผู้ลงนามหลัก (is_primary = true)
        setSelectedSignerIds(signers.filter(s => s.is_primary).map(s => s.id));
        const company = companies.find(c => c.id === companyId);
        if (company) {
          const minRequired = company.min_required_signers ?? company.minRequiredSigners ?? 1;
          setMinRequiredSigners(minRequired);
        }
      } catch (error) {
        console.error('Error loading authorized signers:', error);
        // ไม่แสดง error เพราะบริษัทอาจยังไม่มีผู้ลงนาม
      }
    }
  };

  // ฟังก์ชันจัดการการแนบไฟล์
  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    const newFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type !== 'application/pdf') {
        showAlert('ประเภทไฟล์ไม่ถูกต้อง', 'กรุณาเลือกไฟล์ PDF เท่านั้น', 'error');
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        showAlert('ขนาดไฟล์ใหญ่เกินไป', `ไฟล์ ${file.name} มีขนาดใหญ่เกิน 10MB`, 'error');
        continue;
      }
      newFiles.push(file);
    }
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  // คืน errors ที่เพิ่งคำนวณ — ผู้เรียกต้องใช้ตัวนี้เลื่อนไปช่องแรกที่ผิด ไม่ใช่ state formErrors (BUG-S2266-001)
  const validateForm = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!contractNumber) {
      errors.contract_number = 'กรุณากรอกเลขที่สัญญา';
    }
    if (!selectedCompany) {
      errors.company_id = 'กรุณาเลือกบริษัท';
    }
    if (!contractDate) {
      errors.contract_date = 'กรุณาระบุวันที่ทำสัญญา';
    }
    if (availableSigners.length > 0 && selectedSignerIds.length < minRequiredSigners) {
      errors.signers = `กรุณาเลือกผู้ลงนามอย่างน้อย ${minRequiredSigners} คน`;
    }
    setFormErrors(errors);
    return errors;
  };

  const handleCreateContract = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      const firstErrorField = Object.keys(validationErrors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`) ||
                       document.querySelector(`[data-name="${firstErrorField}"]`) ||
                       document.querySelector(`#${firstErrorField}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (element as HTMLElement).focus?.();
        }
      }
      return;
    }

    const confirmed = await showConfirm(
      'ยืนยันการสร้างสัญญาหลัก',
      `คุณต้องการสร้างสัญญาหลักเลขที่ ${contractNumber} หรือไม่? (เพิ่มสัญญาแนบท้ายได้ภายหลังที่เมนู ⋮ ในหน้าจัดการสัญญา)`,
      () => {},
      {
        confirmText: 'สร้างสัญญาหลัก',
        cancelText: 'ยกเลิก'
      }
    );

    if (!confirmed) return;

    if (!submitGuard.begin()) return;   // duplicate click — already saving

    try {
      setLoading(true);

      // ตรวจสอบว่าเลขที่สัญญาซ้ำหรือไม่
      const codeExists = await contractService.checkCodeExists(contractNumber);
      if (codeExists) {
        await replaceTakenContractNumber(contractNumber);
        setLoading(false);
        return;
      }
      setCodeError('');

      // FB-2026-0004: สัญญาหลัก = วันที่ทำสัญญาอย่างเดียว ไม่มีวันเริ่ม/วันสิ้นสุด (สัญญาแบบเปิด)
      const createdContract = await contractService.createMainContract({
        contract_number: contractNumber,
        company_id: selectedCompany!,
        contract_date: contractDate,
        terms_conditions: termsConditions || undefined,
        notes: notes || undefined,
        signer_ids: selectedSignerIds.length > 0 ? selectedSignerIds : undefined,
      });

      if (!createdContract || !createdContract.id) {
        throw new Error('ไม่สามารถรับข้อมูลสัญญาที่สร้างได้');
      }
      const createdNumber = createdContract.contract_number || contractNumber;
      const nextStepHint = 'เพิ่มสัญญาแนบท้ายได้ที่เมนู ⋮ ในหน้าจัดการสัญญา หรือในหน้ารายละเอียดสัญญา';

      // อัพโหลดไฟล์แนบ (ถ้ามี)
      if (attachedFiles.length > 0) {
        try {
          for (const file of attachedFiles) {
            await contractService.uploadContractDocument(createdContract.id, file);
          }
          // KD3 S2121 (BUG-S2121-002): แสดงเสมอ
          showAlert('สร้างสัญญาหลักสำเร็จ!', `สร้างสัญญาหลักเลขที่ ${createdNumber} และอัพโหลดไฟล์แนบ ${attachedFiles.length} ไฟล์เรียบร้อยแล้ว · ${nextStepHint}`, 'success');
        } catch (uploadError: any) {
          console.error('Error uploading files:', uploadError);
          showAlert('สร้างสัญญาสำเร็จ แต่มีปัญหาในการอัพโหลดไฟล์',
                   `สัญญาถูกสร้างแล้ว แต่ไม่สามารถอัพโหลดไฟล์แนบได้: ${uploadError.message || 'Unknown error'}`,
                   'warning');
        }
      } else {
        showAlert('สร้างสัญญาหลักสำเร็จ!', `สร้างสัญญาหลักเลขที่ ${createdNumber} เรียบร้อยแล้ว · ${nextStepHint}`, 'success');
      }

      setIsFormDirty(false);
      navigate(`/contracts/view/${createdContract.id}`);
    } catch (error: any) {
      console.error('Error creating contract:', error);

      // อีกคนบันทึกเลขเดียวกันไปก่อน (ผ่านการตรวจซ้ำพร้อมกัน) → ออกเลขใหม่ให้แล้วให้กดบันทึกซ้ำ
      const dupDetail = error.response?.status === 400 ? getApiErrorMessage(error, '') : '';
      if (dupDetail.includes(`เลขที่สัญญา ${contractNumber} ถูกใช้งานแล้ว`)) {
        await replaceTakenContractNumber(contractNumber);
        return;
      }

      // BUG-S2084-002: ใช้ตัวกลางที่รู้จักทั้ง envelope ของ KRP และกรณีไม่มี response
      showAlert('เกิดข้อผิดพลาด', getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการสร้างสัญญา'), 'error');
    } finally {
      submitGuard.end();
      setLoading(false);
    }
  };

  return (
    <Layout>
      <ConfirmModal
        isOpen={showModal}
        onClose={onCancelLeave}
        onConfirm={onConfirmLeave}
        pushHistoryOnOpen={false}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <ConfirmModal
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={() => { setShowBackConfirm(false); setIsFormDirty(false); navigate('/contracts'); }}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <div className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiFileAddLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">สร้างสัญญาหลัก</h1>
            <p className="text-xs text-secondary-500 mt-0.5">สัญญาเช่าอุปกรณ์ K-Product ต่อบริษัท · สัญญาแบบเปิด ไม่มีวันสิ้นสุด</p>
          </div>
        </div>
      </div>

        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg border border-secondary-200">
            <div className="px-6 py-6">
              {/* ข้อมูลสัญญา */}
              <div className="mb-8">
                <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                  <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shadow-sm">
                    <RiInformationLine className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-secondary-800">ข้อมูลสัญญาหลัก</h2>
                </div>

                <div className="mb-4 p-4 bg-info-50 border border-info-200 rounded-lg">
                  <div className="flex items-start">
                    <RiFileListLine className="h-5 w-5 text-info-600 mr-2 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-info-800">สัญญาหลักใส่เฉพาะวันที่ทำสัญญา</h3>
                      <p className="text-xs text-info-700 mt-1">
                        สัญญาหลักเป็นสัญญาแบบเปิด ไม่มีวันเริ่ม/วันสิ้นสุด · อุปกรณ์แต่ละเครื่องผูกผ่าน "สัญญาแนบท้าย" (บันทึกส่ง-มอบ)
                        1 ฉบับต่อ 1 รายการเช่า — เพิ่มได้ที่เมนู ⋮ ในหน้าจัดการสัญญา หลังสร้างสัญญาหลักแล้ว
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* เลขที่สัญญา */}
                  <div>
                    <ValidatedInput
                      label="เลขที่สัญญา"
                      name="contract_number"
                      value={contractNumber}
                      onChange={(e) => setContractNumber(e.target.value)}
                      required
                      readOnly
                      placeholder="กำลังสร้างเลขที่สัญญา..."
                      error={codeError}
                    />
                  </div>

                  {/* บริษัท */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      บริษัท (ผู้เช่า) <span className="text-danger-500">*</span>
                    </label>
                    <SearchableSelect
                      name="company_id"
                      options={companies.map(company => ({
                        value: company.id,
                        label: `${company.code} - ${company.name}`
                      }))}
                      value={selectedCompany || ''}
                      onChange={(value) => {
                        handleCompanyChange(Number(value));
                        if (formErrors.company_id) {
                          setFormErrors(prev => ({ ...prev, company_id: '' }));
                        }
                      }}
                      placeholder="-- เลือกบริษัท --"
                      searchPlaceholder="ค้นหาบริษัท..."
                      className="w-full"
                      error={formErrors.company_id}
                    />
                    {/* G5: คำเตือนแบบไม่บล็อก — ไม่ล้างบริษัทที่เลือก ทำสัญญาต่อได้เลย
                        OBSERVATION 3: แยก "มีผลแล้ว (ACTIVE)" ออกจาก "ร่างที่ยังไม่อนุมัติ (DRAFT)" */}
                    {selectedCompany && contractSummary &&
                      (contractSummary.activeTotal > 0 || contractSummary.draftTotal > 0) && (
                      <div className="mt-2 rounded-md border border-warning-200 bg-warning-50 p-3 text-xs text-warning-800" role="status">
                        {companyContractWarningLines(contractSummary).map((line, i) => (
                          <p key={i} className={i > 0 ? 'mt-1 font-medium' : 'font-medium'} data-testid="company-contract-count">
                            {line}
                          </p>
                        ))}
                        {contractSummary.mainContracts.length > 0 ? (
                          <p className="mt-1 font-semibold" data-testid="existing-main-warning">
                            บริษัทนี้มีสัญญาหลักอยู่แล้ว ({contractSummary.mainContracts.map((m) => m.number).join(', ')})
                            {' '}— ถ้าจะเพิ่มอุปกรณ์ ให้ "เพิ่มสัญญาแนบท้าย" ที่สัญญาหลักเดิมแทนการสร้างสัญญาหลักฉบับใหม่
                            (ยังสร้างฉบับใหม่ได้ถ้าตั้งใจ)
                          </p>
                        ) : (
                          <p className="mt-1">
                            ตรวจก่อนว่าไม่ได้ออกสัญญาหลักซ้ำซ้อนกับฉบับเดิม
                          </p>
                        )}
                        <Link to="/contracts" target="_blank" rel="noopener noreferrer" className="mt-1 inline-block font-medium underline">
                          เปิดรายการสัญญาเพื่อตรวจสอบ
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* วันที่ทำสัญญา */}
                  <div>
                    <label htmlFor="contract_date" className="block text-sm font-medium text-secondary-700 mb-2">
                      วันที่ทำสัญญา <span className="text-danger-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="contract_date"
                      name="contract_date"
                      value={contractDate}
                      onChange={(e) => {
                        setContractDate(e.target.value);
                        if (formErrors.contract_date) {
                          setFormErrors(prev => ({ ...prev, contract_date: '' }));
                        }
                      }}
                      className={`input ${formErrors.contract_date ? 'border-danger-500' : ''}`}
                      aria-invalid={Boolean(formErrors.contract_date)}
                      aria-describedby={formErrors.contract_date ? 'contract_date-error' : undefined}
                    />
                    {formErrors.contract_date && (
                      <p id="contract_date-error" className="mt-1 text-sm text-danger-600">{formErrors.contract_date}</p>
                    )}
                  </div>

                  {/* ผู้มีอำนาจลงนาม */}
                  {selectedCompany && availableSigners.length > 0 && (
                    <div className="col-span-2">
                      <label id="signers-label" className="block text-sm font-medium text-secondary-700 mb-2">
                        ผู้มีอำนาจลงนาม (ฝั่งผู้เช่า) <span className="text-danger-500">*</span>
                        {minRequiredSigners > 1 && (
                          <span className="text-xs text-secondary-500 ml-2">
                            (ต้องเลือกอย่างน้อย {minRequiredSigners} คน)
                          </span>
                        )}
                      </label>
                      {/* BUG-S2085-004: กลุ่ม checkbox ต้องมีชื่อกลุ่ม + ผูกข้อความ error ให้ screen reader อ่านตอนโฟกัส */}
                      <div
                        role="group"
                        aria-labelledby="signers-label"
                        aria-describedby={formErrors.signers ? 'signers-error' : undefined}
                        className="space-y-2 border border-secondary-200 rounded-lg p-3"
                      >
                        {availableSigners.map(signer => (
                          <label key={signer.id} className="flex items-center gap-3 p-2 rounded hover:bg-secondary-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedSignerIds.includes(signer.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedSignerIds(prev => [...prev, signer.id]);
                                } else {
                                  setSelectedSignerIds(prev => prev.filter(id => id !== signer.id));
                                }
                                if (formErrors.signers) {
                                  setFormErrors(prev => ({ ...prev, signers: '' }));
                                }
                              }}
                              className="rounded border-secondary-300 text-primary-600"
                            />
                            <div>
                              <span className="font-medium text-secondary-900">{signer.full_name}</span>
                              <span className="text-sm text-secondary-500 ml-2">({signer.position})</span>
                              {signer.is_primary && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs bg-primary-100 text-primary-700 rounded">หลัก</span>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                      {formErrors.signers && (
                        <p id="signers-error" className="mt-1 text-sm text-danger-600">{formErrors.signers}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ข้อกำหนดและเงื่อนไขเพิ่มเติม */}
              <div className="mb-8">
                <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                  <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                    <RiFileTextLine className="w-4 h-4 text-white" />
                  </div>
                  <h3 id="contract-create-terms-heading" className="text-base font-semibold text-secondary-800">ข้อกำหนดและเงื่อนไขเพิ่มเติม</h3>
                </div>
                <textarea
                  aria-labelledby="contract-create-terms-heading"
                  value={termsConditions}
                  onChange={(e) => setTermsConditions(e.target.value)}
                  rows={4}
                  className="input"
                  placeholder="ระบุข้อกำหนดและเงื่อนไขเพิ่มเติม (ถ้ามี)"
                />
              </div>

              {/* หมายเหตุ */}
              <div className="mb-8">
                <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                  <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                    <RiFileTextLine className="w-4 h-4 text-white" />
                  </div>
                  <h3 id="contract-create-notes-heading" className="text-base font-semibold text-secondary-800">หมายเหตุ</h3>
                </div>
                <textarea
                  aria-labelledby="contract-create-notes-heading"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
                />
              </div>

              {/* แนบเอกสาร */}
              <div className="mb-8">
                <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                  <div className="w-8 h-8 bg-info-600 rounded-md flex items-center justify-center shadow-sm">
                    <RiAttachmentLine className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-secondary-800">แนบเอกสาร (ไม่บังคับ)</h3>
                </div>
                
                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragOver 
                      ? 'border-info-400 bg-info-50' 
                      : 'border-secondary-300 hover:border-secondary-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <RiAttachmentLine className="mx-auto h-8 w-8 text-secondary-400 mb-2" />
                  <p className="text-sm text-secondary-600 mb-2">
                    ลากไฟล์ PDF มาวางที่นี่ หรือ
                  </p>
                  {/* sr-only (ไม่ใช่ hidden) — display:none ทำให้ Tab ข้ามช่องนี้ แนบไฟล์ด้วยคีย์บอร์ดไม่ได้ (BUG-S2085-001) */}
                  <label className="inline-flex items-center px-4 min-h-[44px] sm:min-h-[38px] py-2 bg-white border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 hover:bg-secondary-50 cursor-pointer focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                    <input
                      type="file"
                      multiple
                      accept=".pdf"
                      aria-label="เลือกไฟล์ PDF แนบสัญญา"
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="sr-only"
                    />
                    เลือกไฟล์ PDF
                  </label>
                  <p className="text-xs text-secondary-500 mt-2">
                    รองรับไฟล์ PDF เท่านั้น ขนาดไม่เกิน 10MB ต่อไฟล์
                  </p>
                </div>

                {/* แสดงรายการไฟล์ที่แนบ */}
                {attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-secondary-900 mb-2">
                      ไฟล์ที่แนบ ({attachedFiles.length} ไฟล์)
                    </h4>
                    <div className="space-y-2">
                      {attachedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-md">
                          <div className="flex items-center gap-3">
                            <RiFileTextLine className="text-danger-500" />
                            <div>
                              <p className="text-sm font-medium text-secondary-900">{file.name}</p>
                              <p className="text-xs text-secondary-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            aria-label={`นำไฟล์ ${file.name} ออก`}
                            title="นำไฟล์ออก"
                            className="text-danger-600 hover:text-danger-800 p-1"
                          >
                            <RiDeleteBinLine className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ปุ่มดำเนินการ */}
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-6 border-t">
                <button
                  onClick={handleBack}
                  className="group px-6 py-2 border border-secondary-300 rounded-lg text-secondary-700 hover:bg-secondary-50 transition-colors"
                >
                  ยกเลิก
                </button>

                <button
                  onClick={handleCreateContract}
                  disabled={loading}
                  className="group px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-secondary-400 transition-all shadow-sm flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>กำลังสร้างสัญญา...</span>
                    </>
                  ) : (
                    <>
                      <RiFileAddLine className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span>สร้างสัญญาหลัก</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ContractCreatePage;